import { useState } from "react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { GraduationCap, Key, ArrowLeft, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentLogin({ onLoginSuccess }) {
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const autoLoginAttempted = useRef(false);

  useEffect(() => {
    // Check for code in URL params
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    
    if (code && !autoLoginAttempted.current) {
      const sanitized = code.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4);
      if (sanitized.length === 4) {
        setAccessCode(sanitized);
        autoLoginAttempted.current = true;
        
        // Auto-submit login
        const performAutoLogin = async () => {
          setLoading(true);
          try {
            const response = await axios.post(`${API}/auth/student-login`, { 
              access_code: sanitized 
            });
            
            onLoginSuccess(response.data);
            toast.success(`Welcome back, ${response.data.display_name}!`);
            // Explicit navigation removed to allow parent Route to handle redirection
            // based on updated user state. This prevents race conditions where
            // ProtectedRoute redirects to home before user context propagates.
          } catch (error) {
            toast.error(error.response?.data?.detail || "Invalid access code link.");
            setLoading(false);
          }
        };
        
        performAutoLogin();
      }
    }
  }, [location, onLoginSuccess, navigate]);

  const handleCodeChange = (e) => {
    // Only allow alphanumeric, auto-uppercase, max 4 chars
    const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    setAccessCode(sanitized.slice(0, 4));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (accessCode.length !== 4) {
      toast.error("Please enter your 4-character access code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/student-login`, { 
        access_code: accessCode 
      });
      
      onLoginSuccess(response.data);
      toast.success(`Welcome back, ${response.data.display_name}!`);
      // Explicit navigation removed to allow parent Route to handle redirection
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid access code. Ask your parent for the correct code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen graph-paper-bg flex items-center justify-center p-6">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      
      <div className="max-w-md w-full" id="main-content" role="main" tabIndex={-1}>
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6" aria-hidden="true">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h1 
              className="text-3xl font-bold text-gray-900 mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Student Login
            </h1>
            <p className="text-gray-600">
              Enter the access code your parent gave you
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="access-code" className="block text-sm font-medium text-gray-700 mb-2">
                Access Code
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                <Input
                  id="access-code"
                  type="text"
                  placeholder="e.g., 5LD9"
                  value={accessCode}
                  onChange={handleCodeChange}
                  className="pl-10 text-center text-3xl tracking-[0.5em] font-mono uppercase h-16"
                  data-testid="student-access-code-input"
                  aria-label="4-character access code"
                  maxLength={4}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                {accessCode.length}/4 characters
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || accessCode.length !== 4}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
              data-testid="student-login-btn"
              aria-label={loading ? "Logging in" : "Start learning"}
              aria-busy={loading}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" role="progressbar" aria-label="Loading" />
              ) : (
                "Start Learning!"
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center mb-4">
              <Shield className="w-4 h-4 inline mr-1 text-green-600" aria-hidden="true" />
              Safe & secure - no personal information needed
            </p>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="w-full text-gray-600"
              aria-label="Go back to home page"
            >
              <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
